import { useCallback, useEffect, useState, type ReactNode } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate, useParams } from "react-router";
import { api } from "../api";
import { PocketFrame, trainerEmoji } from "../shell";
import {
  APPLICATION_SECTIONS,
  APPLICATION_STATUS_LABELS,
  canSubmit,
  emptyBrief,
  normalizeBrief,
  type ApplicationBrief,
  type ApplicationStatus,
} from "../../domain/applications";

type Profile = {
  displayName: string;
  bio: string;
  specialization: string;
  photo: string | null;
  demo: boolean;
  status: string;
  slug: string | null;
  storefrontUrl: string | null;
  links: Array<{ label: string; url: string }>;
};

type PriceItem = {
  offerVersionId: string;
  accessProductId: string;
  kind: string;
  coverageKind: string;
  trainerSlug: string | null;
  title: string;
  priceKopecks: string;
  enabled: boolean;
  sellerShareKopecks: string;
  feeKopecks: string;
};

function formatRub(kopecks: string | number): string {
  return `${(Number(kopecks) / 100).toLocaleString("ru-RU")} ₽`;
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <PocketFrame wide>
      <div className="shell">
        <aside className="side">
          <div className="brand">Clever</div>
          <nav className="nav">
            <NavLink to="/seller" end>Обзор</NavLink>
            <NavLink to="/seller/profile">Публичный профиль</NavLink>
            <NavLink to="/seller/storefront">Витрина</NavLink>
            <NavLink to="/seller/trainers">Тренажёры</NavLink>
            <NavLink to="/seller/prices">Цены</NavLink>
            <NavLink to="/seller/analytics">Аналитика</NavLink>
            <NavLink to="/seller/statements">Продажи</NavLink>
            <NavLink to="/seller/applications">Заявки</NavLink>
            <NavLink to="/app">Семья</NavLink>
          </nav>
        </aside>
        <main className="main cabinet">{children}</main>
      </div>
      <nav className="bottom">
        <a href="/seller">Обзор</a>
        <a href="/seller/prices">Цены</a>
        <a href="/seller/applications">Заявки</a>
        <a href="/seller/profile">Ещё</a>
      </nav>
    </PocketFrame>
  );
}

function Overview() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [apps, setApps] = useState<Array<{ id: string; statusLabel: string }>>([]);
  useEffect(() => {
    api<Profile>("/api/v1/seller/profile").then(setProfile).catch(() => setProfile(null));
    api<{ items: Array<{ id: string; statusLabel: string }> }>("/api/v1/seller/applications")
      .then((d) => setApps(d.items))
      .catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Обзор</h1>
      <p>Репетитор — автор и продавец, но не учитель конкретного ребёнка. Здесь нет учеников, ответов и семейных данных.</p>
      <div className="grid3">
        <section className="card">
          <h2>Витрина</h2>
          <p>{profile?.displayName ?? "Оформите публичный профиль."}</p>
          {profile?.storefrontUrl ? <a href={profile.storefrontUrl}>{profile.storefrontUrl}</a> : <a href="/seller/profile">Публичный профиль</a>}
        </section>
        <section className="card">
          <h2>Заявки</h2>
          <p>{apps.length ? apps.map((a) => a.statusLabel).join(", ") : "Заявок пока нет."}</p>
          <a className="btn ghost" href="/seller/applications/new">Создать заявку</a>
        </section>
        <section className="card">
          <h2>Аналитика</h2>
          <p>Только агрегированные данные. Имена детей недоступны.</p>
          <a href="/seller/analytics">Открыть</a>
        </section>
      </div>
    </>
  );
}

function ProfilePage() {
  const [form, setForm] = useState({ displayName: "", bio: "", specialization: "", slug: "", instagram: "", other: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [slugState, setSlugState] = useState("");
  useEffect(() => {
    api<Profile>("/api/v1/seller/profile")
      .then((p) => {
        const ig = p.links.find((l) => /instagram/i.test(l.label));
        const other = p.links.find((l) => !/instagram/i.test(l.label));
        setForm({
          displayName: p.displayName,
          bio: p.bio,
          specialization: p.specialization,
          slug: p.slug ?? "",
          instagram: ig?.url ?? "",
          other: other?.url ?? "",
        });
      })
      .catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Публичный профиль</h1>
      <p>Расскажите о подходе без непроверяемых обещаний результата.</p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr("");
          const links = [
            form.instagram ? { label: "Instagram", url: form.instagram } : null,
            form.other ? { label: "Сайт", url: form.other } : null,
          ].filter(Boolean);
          try {
            await api("/api/v1/seller/profile", {
              method: "POST",
              body: JSON.stringify({ ...form, links }),
            });
            setMsg("Сохранено.");
          } catch (ex) {
            setErr((ex as Error).message);
          }
        }}
      >
        <label className="field">Публичное имя<input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} /></label>
        <label className="field">Короткое описание<textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></label>
        <label className="field">Специализация<input value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} /></label>
        <label className="field">
          Адрес витрины
          <input
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            onBlur={async () => {
              if (!form.slug) return;
              const r = await api<{ available: boolean; message: string }>(`/api/v1/seller/slug?value=${encodeURIComponent(form.slug)}`);
              setSlugState(r.message);
            }}
          />
        </label>
        <p className="muted">https://{form.slug || "slug"}.clever.party1207.online — используйте латинские буквы, цифры и дефисы.</p>
        {slugState ? <p>{slugState}</p> : null}
        <label className="field">Instagram<input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="https://" /></label>
        <label className="field">Другие публичные ссылки<input value={form.other} onChange={(e) => setForm({ ...form, other: e.target.value })} placeholder="https://" /></label>
        {err ? <p className="alert">{err}</p> : null}
        {msg ? <p>{msg}</p> : null}
        <button className="btn" type="submit">Сохранить</button>
      </form>
    </>
  );
}

function StorefrontPage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  useEffect(() => {
    api<Profile>("/api/v1/seller/profile").then(setProfile).catch(() => setProfile(null));
  }, []);
  return (
    <>
      <h1>Адрес и preview витрины</h1>
      {profile?.storefrontUrl ? (
        <>
          <p>{profile.storefrontUrl}</p>
          <a className="btn" href={profile.storefrontUrl}>Предпросмотр</a>
        </>
      ) : (
        <p>Сначала выберите адрес в профиле.</p>
      )}
    </>
  );
}

function TrainersPage() {
  const [items, setItems] = useState<Array<{ slug: string; title: string; published: boolean; goal: string }>>([]);
  useEffect(() => {
    api<{ items: typeof items }>("/api/v1/seller/trainers").then((d) => setItems(d.items)).catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Тренажёры</h1>
      {items.length === 0 ? <p>Опубликованных тренажёров пока нет.</p> : null}
      <div className="grid3">
        {items.map((t) => (
          <article key={t.slug} className="card">
            <p className="tile-emoji" aria-hidden="true">{trainerEmoji(t.slug)}</p>
            <h2>{t.title}</h2>
            <p>{t.goal}</p>
            <p>{t.published ? "Доступно покупателям" : "Снято с публикации"}</p>
          </article>
        ))}
      </div>
    </>
  );
}

function PricesPage() {
  const [items, setItems] = useState<PriceItem[]>([]);
  const [msg, setMsg] = useState("");
  const load = () => api<{ items: PriceItem[] }>("/api/v1/seller/prices").then((d) => setItems(d.items)).catch(() => undefined);
  useEffect(() => { load(); }, []);
  return (
    <>
      <h1>Цены и варианты доступа</h1>
      <p>Разовая цена и месячная цена — для тренажёра и для полного каталога.</p>
      {items.map((p) => (
        <div key={p.offerVersionId} className="card">
          <h2>{p.coverageKind === "catalog" ? "Полный каталог" : p.title} · {p.kind === "monthly" ? "месячная" : "разовая"}</h2>
          <label className="field">
            Цена, ₽
            <input
              value={String(Number(p.priceKopecks) / 100)}
              onChange={(e) => {
                const rub = Number(e.target.value);
                const kopecks = Number.isFinite(rub) ? String(Math.round(rub * 100)) : "0";
                setItems((cur) => cur.map((x) => (x.offerVersionId === p.offerVersionId ? { ...x, priceKopecks: kopecks } : x)));
              }}
            />
          </label>
          <label>
            <input
              type="checkbox"
              checked={p.enabled}
              onChange={(e) => setItems((cur) => cur.map((x) => (x.offerVersionId === p.offerVersionId ? { ...x, enabled: e.target.checked } : x)))}
            />{" "}
            Доступно покупателям
          </label>
          <p>Доля репетитора, 80%: {formatRub(p.sellerShareKopecks)}</p>
          <p>Комиссия Clever, 20%: {formatRub(p.feeKopecks)}</p>
        </div>
      ))}
      <button
        className="btn"
        type="button"
        onClick={async () => {
          await api("/api/v1/seller/prices", {
            method: "POST",
            body: JSON.stringify({
              items: items.map((p) => ({
                accessProductId: p.accessProductId,
                kind: p.kind,
                priceKopecks: p.priceKopecks,
                enabled: p.enabled,
              })),
            }),
          });
          setMsg("Цены сохранены.");
          load();
        }}
      >Сохранить</button>
      {msg ? <p>{msg}</p> : null}
    </>
  );
}

function AnalyticsPage() {
  const [data, setData] = useState<{
    note: string;
    suppressed: boolean;
    label: string | null;
    storefrontViews: number | null;
    trialStarts: number | null;
    paidCount: number | null;
    demoSlice: { marked: string; suppressed: boolean; label: string | null } | null;
  } | null>(null);
  useEffect(() => {
    api<NonNullable<typeof data>>("/api/v1/seller/analytics").then(setData).catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Агрегированная аналитика</h1>
      <p>{data?.note}</p>
      {data?.suppressed ? <p>{data.label}</p> : (
        <ul>
          <li>Просмотры витрины: {data?.storefrontViews ?? "—"}</li>
          <li>Старты пробного доступа: {data?.trialStarts ?? "—"}</li>
          <li>Тестовые покупки: {data?.paidCount ?? "—"}</li>
        </ul>
      )}
      {data?.demoSlice ? (
        <p className="badge">{data.demoSlice.marked}. Демонстрационные и рабочие показатели показываются отдельно.</p>
      ) : null}
    </>
  );
}

function StatementsPage() {
  const [data, setData] = useState<{ note: string; periods: Array<{ period: string; grossKopecks: string; feeKopecks: string; netKopecks: string; paidCount: number }> } | null>(null);
  useEffect(() => {
    api<NonNullable<typeof data>>("/api/v1/seller/statements").then(setData).catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Расчётный доход и mock-ledger</h1>
      <p>{data?.note}</p>
      {data?.periods.length ? (
        <table className="progress-table">
          <thead><tr><th>Период</th><th>Валовый объём</th><th>Комиссия</th><th>Доля репетитора</th><th>Оплат</th></tr></thead>
          <tbody>
            {data.periods.map((p) => (
              <tr key={p.period}>
                <td>{p.period}</td>
                <td>{formatRub(p.grossKopecks)}</td>
                <td>{formatRub(p.feeKopecks)}</td>
                <td>{formatRub(p.netKopecks)}</td>
                <td>{p.paidCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p>За выбранный период данных пока нет.</p>}
    </>
  );
}

function ApplicationsList() {
  const [items, setItems] = useState<Array<{ id: string; status: ApplicationStatus; statusLabel: string; updatedAt: string }>>([]);
  useEffect(() => {
    api<{ items: typeof items }>("/api/v1/seller/applications").then((d) => setItems(d.items)).catch(() => undefined);
  }, []);
  return (
    <>
      <h1>Заявки на новые тренажёры</h1>
      <a className="btn" href="/seller/applications/new">Создать заявку</a>
      {items.length === 0 ? <p>Заявок пока нет.</p> : null}
      {items.map((a) => (
        <article key={a.id} className="card">
          <p><span className="badge">{a.statusLabel}</span></p>
          <a href={`/seller/applications/${a.id}`}>Открыть бриф</a>
        </article>
      ))}
    </>
  );
}

function Wizard({ id }: { id?: string }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [brief, setBrief] = useState<ApplicationBrief>(emptyBrief());
  const [status, setStatus] = useState<ApplicationStatus>("draft");
  const [appId, setAppId] = useState(id);
  const [saved, setSaved] = useState("");
  const [err, setErr] = useState("");
  const [lockVersion, setLockVersion] = useState(1);
  const section = APPLICATION_SECTIONS[step]!;

  const persist = useCallback(async (next = brief) => {
    if (!appId) {
      const created = await api<{ id: string; brief: ApplicationBrief }>("/api/v1/applications", {
        method: "POST",
        body: JSON.stringify({ brief: next }),
      });
      setAppId(created.id);
      if (!id) nav(`/seller/applications/${created.id}`, { replace: true });
      return created.id;
    }
    const res = await api<{ lockVersion: number; savedAt: string; status: ApplicationStatus }>(`/api/v1/applications/${appId}`, {
      method: "POST",
      headers: { "if-match": String(lockVersion) },
      body: JSON.stringify({ brief: next }),
    });
    setLockVersion(res.lockVersion);
    setStatus(res.status);
    setSaved(new Date(res.savedAt).toLocaleTimeString("ru-RU"));
    return appId;
  }, [appId, brief, id, lockVersion, nav]);

  useEffect(() => {
    if (!id) return;
    api<{ brief: ApplicationBrief; status: ApplicationStatus; lockVersion: number; savedAt: string }>(`/api/v1/applications/${id}`)
      .then((d) => {
        setBrief(normalizeBrief(d.brief));
        setStatus(d.status);
        setLockVersion(d.lockVersion);
        setSaved(new Date(d.savedAt).toLocaleTimeString("ru-RU"));
      })
      .catch(() => undefined);
  }, [id]);

  useEffect(() => {
    if (!appId) return;
    const t = window.setInterval(() => { persist().catch(() => undefined); }, 10000);
    return () => window.clearInterval(t);
  }, [appId, persist]);

  const editable = status === "draft" || status === "clarification";

  return (
    <>
      <h1>Бриф нового тренажёра</h1>
      <p className="badge">{APPLICATION_STATUS_LABELS[status]}</p>
      {saved ? <p className="muted">Черновик сохранён в {saved}</p> : null}
      <ol className="wizard-steps">
        {APPLICATION_SECTIONS.map((s, i) => (
          <li key={s.id}><button type="button" className={i === step ? "btn steel" : "btn ghost"} onClick={() => setStep(i)}>{s.id}. {s.title}</button></li>
        ))}
      </ol>
      <section className="card">
        <h2>{section.title}</h2>
        {section.fields.map((f) => (
          <label key={f.key} className="field">
            {f.label}
            <textarea
              value={brief[f.key]}
              disabled={!editable}
              onChange={(e) => setBrief({ ...brief, [f.key]: e.target.value })}
              onBlur={() => persist().catch((ex) => setErr((ex as Error).message))}
            />
          </label>
        ))}
        {step === 10 ? (
          <label>
            <input
              type="checkbox"
              checked={brief.rightsDeclaration}
              disabled={!editable}
              onChange={(e) => setBrief({ ...brief, rightsDeclaration: e.target.checked })}
            />{" "}
            Я подтверждаю права на материалы
          </label>
        ) : null}
        <p>Не добавляйте персональные данные конкретных учеников. Не загружайте фотографии, голос, переписку или ответы детей.</p>
      </section>
      <div className="row">
        <button className="btn ghost" type="button" disabled={step === 0} onClick={() => setStep(step - 1)}>Назад</button>
        <button className="btn" type="button" disabled={step === 10} onClick={() => setStep(step + 1)}>Дальше</button>
        <button className="btn ghost" type="button" onClick={() => persist().then(() => setSaved(new Date().toLocaleTimeString("ru-RU")))}>Сохранить черновик</button>
        {step === 10 ? (
          <button
            className="btn steel"
            type="button"
            disabled={!canSubmit(brief) || !editable}
            onClick={async () => {
              try {
                const currentId = await persist();
                const res = await api<{ status: ApplicationStatus }>(`/api/v1/applications/${currentId}/submit`, {
                  method: "POST",
                  body: JSON.stringify({ comment: "Отправлена" }),
                });
                setStatus(res.status);
              } catch (ex) {
                setErr((ex as Error).message);
              }
            }}
          >Отправить заявку</button>
        ) : null}
        {(status === "ready" || status === "rejected") && appId ? (
          <button
            className="btn"
            type="button"
            onClick={async () => {
              const copy = await api<{ id: string }>(`/api/v1/applications/${appId}/duplicate`, { method: "POST", body: "{}" });
              nav(`/seller/applications/${copy.id}`);
            }}
          >Создать черновик из этой заявки</button>
        ) : null}
      </div>
      {err ? <p className="alert">{err}</p> : null}
    </>
  );
}

function WizardRoute() {
  const { id } = useParams();
  return <Wizard id={id} />;
}

export function SellerCabinet() {
  const [me, setMe] = useState<{ email?: string } | null | undefined>(undefined);
  useEffect(() => {
    api<{ email?: string }>("/api/v1/me").then(setMe).catch(() => setMe(null));
  }, []);
  if (me === undefined) return <p className="page">…</p>;
  if (!me) return <Navigate to="/login?next=/seller" />;
  return (
    <Shell>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="storefront" element={<StorefrontPage />} />
        <Route path="trainers" element={<TrainersPage />} />
        <Route path="prices" element={<PricesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="statements" element={<StatementsPage />} />
        <Route path="applications" element={<ApplicationsList />} />
        <Route path="applications/new" element={<Wizard />} />
        <Route path="applications/:id" element={<WizardRoute />} />
      </Routes>
    </Shell>
  );
}
